// =====================================================================
// paginas/funcionarios.js — RH e custos recorrentes.
//
// Funcionário ≠ usuário. Um é pessoa (tem salário, admissão, prédio de
// lotação); o outro é login. O zelador pode não ter acesso nenhum.
//
// O salário vira custo recorrente: lança sozinho todo mês, em vez de
// alguém digitar 12 vezes por ano.
// =====================================================================

import { sb } from '../../../../packages/ui/cliente.js';
import { icon, escapeHtml, tabela, badge, modal, cabecalho, checar } from '../ui.js';
import { moeda, data } from '../../../../packages/ui/format.js';

export async function pagFuncionarios(el, estado) {
  const [func, custos, predios, cats, contas] = await Promise.all([
    sb.from('funcionarios').select('*,predios(nome)').order('nome'),
    sb.from('custos_recorrentes').select('*,categorias(nome)').eq('ativo', true).order('descricao'),
    sb.from('predios').select('id,nome').order('nome'),
    sb.from('categorias').select('id,nome').eq('tipo', 'saida').order('nome'),
    sb.from('contas_financeiras').select('id,nome').eq('ativa', true).order('nome')
  ]);

  const lista = checar(func);
  const rec = checar(custos);
  const folha = lista.filter(f => f.ativo).reduce((s, f) => s + Number(f.salario || 0), 0);
  const mensal = rec.reduce((s, c) => s + Number(c.valor), 0);

  el.innerHTML = '';
  el.appendChild(cabecalho({
    titulo: 'Funcionários',
    acao: 'Novo funcionário',
    onAcao: () => formFunc(null, checar(predios), estado, () => pagFuncionarios(el, estado))
  }));

  const g = document.createElement('div');
  g.className = 'grade k4';
  g.style.marginBottom = '20px';
  g.innerHTML = `
    <div class="cartao kpi"><span class="rotulo">Folha mensal</span>
      <span class="valor">${moeda(folha)}</span>
      <span class="nota">${lista.filter(f => f.ativo).length} ativo(s)</span></div>
    <div class="cartao kpi"><span class="rotulo">Custos recorrentes</span>
      <span class="valor">${moeda(mensal)}</span>
      <span class="nota">${rec.length} lançamento(s) automático(s)/mês</span></div>`;
  el.appendChild(g);

  const t1 = document.createElement('div');
  t1.innerHTML = tabela([
    { th: 'Nome', td: f => `<strong>${escapeHtml(f.nome)}</strong>` },
    { th: 'Cargo', td: f => escapeHtml(f.cargo || '—') },
    { th: 'Prédio', td: f => escapeHtml(f.predios?.nome || '—') },
    { th: 'Admissão', td: f => f.admissao ? data(f.admissao) : '—' },
    { th: 'Salário', cls: 'num', td: f => f.salario ? moeda(f.salario) : '—' },
    { th: 'Situação', td: f => badge(f.ativo ? 'ativo' : 'desligado', f.ativo ? 'verde' : 'cinza') },
    { th: '', cls: 'acoes', td: f => `<button class="icone" data-ed="${f.id}">${icon('editar')}</button>` }
  ], lista, 'Nenhum funcionário cadastrado.');
  t1.querySelectorAll('[data-ed]').forEach(b => b.onclick = () =>
    formFunc(lista.find(f => f.id === b.dataset.ed), checar(predios), estado,
             () => pagFuncionarios(el, estado)));
  el.appendChild(t1.firstElementChild);

  const h = document.createElement('div');
  h.className = 'cabecalho';
  h.style.marginTop = '28px';
  h.innerHTML = `<h2 style="font-size:16px">${icon('financeiro')} Custos recorrentes</h2>
    <button class="primaria" id="nc">${icon('novo')} Novo custo</button>`;
  el.appendChild(h);
  h.querySelector('#nc').onclick = () => formCusto(checar(cats), checar(contas), checar(predios),
    estado, () => pagFuncionarios(el, estado));

  const t2 = document.createElement('div');
  t2.innerHTML = tabela([
    { th: 'Descrição', td: c => escapeHtml(c.descricao) },
    { th: 'Categoria', td: c => escapeHtml(c.categorias?.nome || '—') },
    { th: 'Valor', cls: 'num', td: c => moeda(c.valor) },
    { th: 'Vencimento', td: c => `dia ${c.dia_vencimento}` }
  ], rec, 'Nenhum custo recorrente. Cadastre internet, contabilidade, salários — o sistema lança todo mês.');
  el.appendChild(t2.firstElementChild);
}

function formFunc(f, predios, estado, aoSalvar) {
  modal({
    titulo: f ? `Funcionário — ${f.nome}` : 'Novo funcionário',
    dados: f || {},
    campos: [
      { nome: 'nome', label: 'Nome', req: true, full: true },
      { nome: 'cpf', label: 'CPF' },
      { nome: 'cargo', label: 'Cargo' },
      { nome: 'telefone', label: 'Telefone' },
      { nome: 'email', label: 'E-mail', tipo: 'email' },
      { nome: 'admissao', label: 'Admissão', tipo: 'date' },
      { nome: 'salario', label: 'Salário', tipo: 'number', step: '0.01' },
      { nome: 'predio_id', label: 'Prédio (se lotado em um)', tipo: 'select',
        opcoes: [{ valor: '', texto: '— nenhum —' }, ...predios.map(p => ({ valor: p.id, texto: p.nome }))] },
      { nome: 'ativo', label: 'Ativo', tipo: 'checkbox', valor: true }
    ],
    onSalvar: async (d) => {
      d.salario = d.salario ? Number(d.salario) : null;
      d.tenant_id = estado.perfil.tenant_id;
      if (f) checar(await sb.from('funcionarios').update(d).eq('id', f.id));
      else   checar(await sb.from('funcionarios').insert(d));
      aoSalvar();
    }
  });
}

function formCusto(cats, contas, predios, estado, aoSalvar) {
  modal({
    titulo: 'Novo custo recorrente',
    aviso: 'Cadastre uma vez; o sistema lança todo mês no livro-caixa.',
    campos: [
      { nome: 'descricao', label: 'Descrição', req: true, full: true, dica: 'ex.: Internet do prédio Regina' },
      { nome: 'valor', label: 'Valor', tipo: 'number', step: '0.01', req: true },
      { nome: 'dia_vencimento', label: 'Dia do vencimento', tipo: 'number', valor: 10 },
      { nome: 'categoria_id', label: 'Categoria', tipo: 'select',
        opcoes: [{ valor: '', texto: '—' }, ...cats.map(c => ({ valor: c.id, texto: c.nome }))] },
      { nome: 'conta_id', label: 'Conta', tipo: 'select',
        opcoes: [{ valor: '', texto: '—' }, ...contas.map(c => ({ valor: c.id, texto: c.nome }))] },
      { nome: 'predio_id', label: 'Prédio', tipo: 'select',
        opcoes: [{ valor: '', texto: '— geral —' }, ...predios.map(p => ({ valor: p.id, texto: p.nome }))] }
    ],
    onSalvar: async (d) => {
      checar(await sb.from('custos_recorrentes').insert({
        ...d, valor: Number(d.valor), dia_vencimento: Number(d.dia_vencimento || 10),
        tenant_id: estado.perfil.tenant_id
      }));
      aoSalvar();
    }
  });
}
